/**
 * Iter 241 (Lot 4 polish) — Panel Pointages sur la fiche équipe.
 *
 * Affiche :
 * - Statut CallMeBot WhatsApp de l'employé (configuré ou non)
 * - Stats des 30 derniers jours (total heures, jours travaillés, à valider)
 * - Liste des pointages récents (date, durée, géofence, statut)
 * - Bouton "Pointer pour ce salarié" (start ou stop selon le contexte)
 */
import React, { useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../ui/button";
import {
  Clock, MessageCircle, MapPin, CheckCircle2, AlertTriangle,
  Loader2, Play, Square, RefreshCw, X,
} from "lucide-react";

export default function TeamPunchPanel({ userId }) {
  const { axiosAuth, user: currentUser } = useAuth();
  const api = axiosAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(null); // null | "start" | "stop"
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const isAdmin = ["admin", "superadmin", "tenant_admin"].includes(currentUser?.role);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/tracking/employees/${userId}/summary?days=30`);
      setData(r.data);
    } catch (e) {
      console.error("punch summary failed", e);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (userId) load(); }, [userId]); // eslint-disable-line

  const adminPunch = async () => {
    if (!reason.trim() || reason.trim().length < 3) {
      alert("Motif obligatoire (min 3 caractères)");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/tracking/admin/punch-for-user", {
        target_user_id: userId,
        mode: showModal,
        reason: reason.trim(),
      });
      setShowModal(null);
      setReason("");
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="bg-[#14101c] border border-white/10 rounded-xl p-4"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Chargement…</div>;
  if (!data) return null;

  const hasOpenPunch = data.stats.in_progress > 0;

  return (
    <div className="space-y-3" data-testid="team-punch-panel">
      <div className="bg-[#14101c] border border-white/10 rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-xs uppercase tracking-wider text-slate-500 font-bold flex items-center gap-2">
            <Clock className="w-3.5 h-3.5 text-emerald-400" />
            Pointages (30 derniers jours)
          </h4>
          <div className="flex items-center gap-2">
            <button onClick={load} className="text-xs text-slate-400 hover:text-white" title="Rafraîchir">
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
            {isAdmin && (
              <>
                {!hasOpenPunch ? (
                  <Button size="sm" onClick={() => setShowModal("start")} className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/30 text-[10px] h-7 px-2 font-bold" data-testid="admin-punch-start-btn">
                    <Play className="w-3 h-3 mr-1" /> Pointer pour lui
                  </Button>
                ) : (
                  <Button size="sm" onClick={() => setShowModal("stop")} className="bg-rose-500/20 text-rose-300 border border-rose-500/30 hover:bg-rose-500/30 text-[10px] h-7 px-2 font-bold" data-testid="admin-punch-stop-btn">
                    <Square className="w-3 h-3 mr-1" /> Clôturer son pointage
                  </Button>
                )}
              </>
            )}
          </div>
        </div>

        {/* Status CallMeBot */}
        <div className="flex items-center gap-2 mb-3 text-xs">
          <MessageCircle className={`w-3.5 h-3.5 ${data.callmebot_configured ? "text-emerald-400" : "text-amber-400"}`} />
          {data.callmebot_configured ? (
            <span className="text-emerald-300 font-bold">WhatsApp configuré</span>
          ) : (
            <span className="text-amber-300 font-bold">WhatsApp non configuré</span>
          )}
          {data.phone && <span className="text-slate-500">— {data.phone}</span>}
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
          <Stat label="Total heures" value={`${data.stats.total_hours}h`} color="text-cyan-400" />
          <Stat label="Jours travaillés" value={data.stats.days_worked} color="text-emerald-400" />
          <Stat label="Pointages" value={data.stats.total_punches} color="text-white" />
          <Stat label="À valider" value={data.stats.needs_approval} color={data.stats.needs_approval > 0 ? "text-amber-400" : "text-slate-400"} />
        </div>

        {/* Liste */}
        {data.punches.length === 0 ? (
          <p className="text-xs text-slate-500 italic">Aucun pointage sur les 30 derniers jours.</p>
        ) : (
          <div className="space-y-1 max-h-72 overflow-auto" data-testid="punches-list">
            {data.punches.slice(0, 30).map(p => (
              <PunchRow key={p.punch_id} p={p} />
            ))}
            {data.punches.length > 30 && (
              <p className="text-[10px] text-slate-500 text-center pt-1">… et {data.punches.length - 30} autres</p>
            )}
          </div>
        )}
      </div>

      {/* Modal pointer pour lui */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setShowModal(null)}>
          <div className="bg-[#14101c] border border-white/10 rounded-xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()} data-testid="admin-punch-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-white">
                {showModal === "start" ? "Démarrer le pointage" : "Clôturer le pointage"}
              </h3>
              <button onClick={() => setShowModal(null)} className="text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mb-4 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-200">
                Cette action sera enregistrée comme <strong>pointage manuel par un admin</strong> dans l'historique de l'employé. Un motif est obligatoire (audit).
              </p>
            </div>
            <label className="text-[11px] text-slate-400 mb-1 block">Motif obligatoire</label>
            <textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="Ex: oubli pointage matin, problème WhatsApp, etc."
              rows={3}
              className="w-full bg-[#0b0811] border border-white/10 rounded-lg px-3 py-2 text-xs text-white mb-4"
              data-testid="reason-textarea"
            />
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" onClick={() => setShowModal(null)} className="text-xs">Annuler</Button>
              <Button onClick={adminPunch} disabled={submitting || !reason.trim()} className="bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold text-xs" data-testid="admin-punch-confirm-btn">
                {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />}
                Confirmer
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, color = "text-white" }) {
  return (
    <div className="bg-[#0b0811] border border-white/5 rounded-lg p-2">
      <div className={`text-base font-black ${color}`}>{value}</div>
      <div className="text-[9px] text-slate-500 uppercase tracking-wider">{label}</div>
    </div>
  );
}

function PunchRow({ p }) {
  const dur = p.duration_minutes != null ? p.duration_minutes : null;
  const start = p.start_at ? new Date(p.start_at) : null;
  const stop = p.stop_at ? new Date(p.stop_at) : null;
  const isAdmin = p.method === "admin_manual";
  const needsApproval = !p.manager_approval && p.geofence_status === "outside";

  return (
    <div className="bg-[#0b0811] border border-white/5 rounded-lg px-3 py-2 flex items-center gap-2 text-[11px]" data-testid={`punch-${p.punch_id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-slate-300">{start?.toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
          <span className="text-slate-600">→</span>
          {stop ? (
            <span className="text-slate-300">{stop.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</span>
          ) : (
            <span className="text-emerald-400 font-bold">En cours</span>
          )}
          {dur != null && <span className="text-cyan-400 font-bold">· {Math.floor(dur / 60)}h{String(dur % 60).padStart(2, "0")}</span>}
        </div>
        <div className="flex items-center gap-2 text-slate-500 text-[10px]">
          {p.geofence_status && (
            <span className="flex items-center gap-1">
              <MapPin className="w-2.5 h-2.5" />
              {p.geofence_status === "inside" ? "Dans la zone" : p.geofence_status === "outside" ? "Hors zone" : p.geofence_status === "manual_admin" ? "Manuel admin" : p.geofence_status}
            </span>
          )}
          {isAdmin && p.admin_punched_by_name && <span className="text-purple-400">· par {p.admin_punched_by_name}</span>}
          {p.method === "qr_code" && <span>· QR code</span>}
        </div>
      </div>
      {needsApproval && (
        <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 font-bold uppercase">À valider</span>
      )}
      {p.manager_approval?.decision === "approved" && (
        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
      )}
    </div>
  );
}
